import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Alert,
  Divider,
  Space,
  message
} from 'antd';
import { authAxios } from '../utils/session_utils';
import { YouTubeLiveStream, Video, YouTubeStreamVideo } from './types';
import { formatDuration, calculateTotalDuration } from './utils';
import VideoSelector from './VideoSelector';
import StreamTimeline from './StreamTimeline';

interface VideoManagerProps {
  stream: YouTubeLiveStream;
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  onPrepareStream?: (stream: YouTubeLiveStream) => void;
  distributionPlatformData?: any[];
  embedded?: boolean;
}

const VideoManager: React.FC<VideoManagerProps> = ({ stream, open, onClose, onSave, onPrepareStream, distributionPlatformData, embedded = false }) => {
  const [selectedVideos, setSelectedVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const loadVideosWithCaptions = async () => {
      if (stream && open) {
        // Load existing videos if available
        if (stream.videos && stream.videos.length > 0) {
          const convertedVideos: Video[] = await Promise.all(
            stream.videos
              .sort((a, b) => a.sequence_order - b.sequence_order)
              .map(async (streamVideo: YouTubeStreamVideo) => {
                // Parse selected_caption_ids if available
                let selectedCaptionIds: string[] = [];
                let selectedCaptions: any[] = [];
                
                if ((streamVideo as any).selected_caption_ids) {
                  try {
                    const captionIds = typeof (streamVideo as any).selected_caption_ids === 'string'
                      ? JSON.parse((streamVideo as any).selected_caption_ids)
                      : (streamVideo as any).selected_caption_ids;
                    
                    if (Array.isArray(captionIds) && captionIds.filter(Boolean).length > 0) {
                      selectedCaptionIds = captionIds;
                      
                      // Fetch caption details from backend (YouTube Live module)
                      try {
                        const response = await authAxios({
                          method: 'GET',
                          url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/captions/by-ids`,
                          params: { ids: captionIds.join(',') }
                        });
                        
                        if (response.data && response.data.captions) {
                          selectedCaptions = response.data.captions;
                        }
                      } catch (fetchError) {
                        console.error('Error fetching caption details:', fetchError);
                      }
                    }
                  } catch (e) {
                    console.error('Error parsing selected_caption_ids:', e);
                  }
                }
                
                return {
                  id: streamVideo.video_id,
                  ref_id: streamVideo.video_id,
                  title: streamVideo.video_title || 'Unknown Title',
                  duration: streamVideo.duration_seconds,
                  url: streamVideo.video_url || '',
                  file_name: streamVideo.video_file_name || '',
                  language: 'en',
                  created_at: streamVideo.created_at,
                  caption_url: streamVideo.caption_url,
                  caption_type: streamVideo.caption_type,
                  captions: selectedCaptions,
                  selected_caption_ids: selectedCaptionIds,
                  selected_captions: selectedCaptions
                };
              })
          );
          setSelectedVideos(convertedVideos);
        } else {
          setSelectedVideos([]);
        }
      }
    };
    
    loadVideosWithCaptions();
  }, [stream, open]);

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      // Prepare video data with order (implicit from array index) and caption IDs
      const videosData = selectedVideos.map(v => ({
        id: v.id,
        selected_caption_ids: v.selected_caption_ids || []
      }));

      // Single sync API call handles all add/remove/reorder/caption updates
      await authAxios({
        method: 'PUT',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${stream.id}/videos/sync`,
        data: { videos: videosData }
      });

      // When in embedded mode (inside ManageVideoModal), persist video_selection_mode
      if (embedded) {
        await authAxios({
          method: 'PUT',
          url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${stream.id}`,
          data: { video_selection_mode: 'manual' }
        });
      }

      // Update playlist on playout server if playing
      const playlistUpdated = await updatePlayoutPlaylistIfPlaying();

      if (playlistUpdated) {
        message.success('Videos updated and playlist refreshed on playout server');
      } else {
        message.success('Videos updated successfully');
      }

      onSave();
    } catch (error) {
      console.error('Error saving videos:', error);
      setError('Failed to save videos: ' + (error instanceof Error ? error.message : String(error)));
    } finally {
      setLoading(false);
    }
  };

  const handleVideosSelected = (videos: Video[]) => {
    setSelectedVideos(videos);
  };

  // Helper function to update playlist on playout server if stream is playing
  const updatePlayoutPlaylistIfPlaying = async () => {
    if (!stream.playout_server_id) {
      console.log('No playout server assigned, skipping playlist update');
      return false;
    }

    try {
      // Check if stream is playing
      const statusResponse = await authAxios({
        method: 'GET',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${stream.id}/playout-status`
      });
      
      const playoutStatus = statusResponse.data?.playout_status || statusResponse.data;
      console.log('Playout status check:', {
        state: playoutStatus?.state,
        ffmpegRunning: playoutStatus?.ffmpeg?.isRunning,
        fullStatus: playoutStatus
      });
      
      const isPlaying = playoutStatus?.state === 'playing' || 
                       playoutStatus?.ffmpeg?.isRunning === true;
      
      if (isPlaying) {
        // Calculate current minute from uptime if available, otherwise restart from beginning
        let startFromMinute = 0;
        if (playoutStatus?.ffmpeg?.uptime) {
          // uptime is in seconds, convert to minutes
          startFromMinute = Math.floor(playoutStatus.ffmpeg.uptime / 60);
        }
        
        console.log(`Stream is playing, updating playlist. Starting from minute: ${startFromMinute}`);
        
        // Trigger playlist update - this will reload the playlist from database
        const updateResponse = await authAxios({
          method: 'POST',
          url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${stream.id}/start-playout`,
          data: { start_from_minute: startFromMinute },
          timeout: 10000
        });
        
        console.log('Playlist update response:', updateResponse.data);
        
        return true; // Indicates playlist was updated
      } else {
        console.log('Stream is not playing, skipping playlist update');
      }
    } catch (error: any) {
      // Log error details for debugging
      console.error('Error updating playout playlist:', {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
    }
    
    return false; // Playlist was not updated (not playing or error)
  };

  const handleVideosReorder = (reorderedVideos: Video[]) => {
    // Only update local state - changes will be persisted when Save is clicked
    setSelectedVideos(reorderedVideos);
  };

  const handleVideoRemove = (videoId: string) => {
    // Only update local state - changes will be persisted when Save is clicked
    const updatedVideos = selectedVideos.filter(v => v.id !== videoId);
    setSelectedVideos(updatedVideos);
  };

  const handleCancel = () => {
    setError('');
    onClose();
  };

  const totalDuration = calculateTotalDuration(selectedVideos);
  const duration24Hours = 24 * 60 * 60; // 24 hours in seconds

  const content = (
    <>
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          closable
          onClose={() => setError('')}
          style={{ marginBottom: '16px' }}
        />
      )}

      <VideoSelector
        selectedVideos={selectedVideos}
        onVideosSelected={handleVideosSelected}
        distributionPlatformData={distributionPlatformData}
      />

      {selectedVideos.length > 0 && (
        <>
          <Divider />

          {totalDuration > duration24Hours && (
            <Alert
              message="Duration Warning"
              description={`Total duration (${formatDuration(totalDuration)}) exceeds 24 hours. Consider splitting into multiple streams.`}
              type="warning"
              style={{ marginBottom: '16px' }}
            />
          )}

          <StreamTimeline
            videos={selectedVideos}
            onVideosReorder={handleVideosReorder}
            onVideoRemove={handleVideoRemove}
            totalDuration={totalDuration}
          />
        </>
      )}

      {embedded && (
        <div style={{ marginTop: '16px', textAlign: 'right' }}>
          <Space>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              type="primary"
              loading={loading}
              onClick={handleSave}
              disabled={selectedVideos.length === 0 && stream.video_selection_mode === 'manual'}
            >
              Save
            </Button>
          </Space>
        </div>
      )}
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Modal
      title={`Manage Videos - ${stream.name}`}
      open={open}
      onCancel={handleCancel}
      width={900}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={loading}
          onClick={handleSave}
          disabled={selectedVideos.length === 0}
        >
          Save
        </Button>
      ]}
    >
      {content}
    </Modal>
  );
};

export default VideoManager;

